import { ChevronDown, ChevronRight, Folder } from "lucide-react";
import { Button } from "@/components/ui/NewButton";
import { useBundleCounts, useBundleTree, useBundleTreeActions, useHasExpandable } from "../bundleTreeStore";
import { GroupType } from "../bundleTypes";
import { ViewEmailGroup } from "./ViewEmailGroup";
import { ViewFile } from "./ViewFile";
import { ViewSection } from "./ViewSection";
import { ViewVolume } from "./ViewVolume";

export const ViewBundleTree = () => {
  const rootChildIds = useBundleTree((s) => s.config.rootChildIds);
  const groupCount = useBundleTree((s) => Object.keys(s.config.groups).length);
  const expandedCount = useBundleTree((s) => s.expandedIds.size);
  const { volumeCount, sectionCount, fileCount } = useBundleCounts();
  const { expandAll, collapseAll } = useBundleTreeActions();
  const allExpanded = groupCount > 0 && expandedCount >= groupCount;
  const hasExpandable = useHasExpandable();

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center">
        {hasExpandable && (
          <div className="flex -ml-2">
            <Button
              onClick={allExpanded ? collapseAll : expandAll}
              size="sm"
              variant="ghost"
              icon={allExpanded ? ChevronDown : ChevronRight}
              tooltip={allExpanded ? "Collapse all" : "Expand all"}
              className="text-gray-500 px-1.5 -ml-2"
            />
          </div>
        )}
        <h3 className="text-sm font-semibold text-gray-700">
          Structure
          <span className="text-xs text-gray-400 font-normal ml-2">
            {[
              volumeCount > 0 && `${volumeCount} volume${volumeCount !== 1 ? 's' : ''}`,
              sectionCount > 0 && `${sectionCount} section${sectionCount !== 1 ? 's' : ''}`,
              fileCount > 0 && `${fileCount} document${fileCount !== 1 ? 's' : ''}`,
            ].filter(Boolean).join(' · ')}
          </span>
        </h3>
      </div>
      {rootChildIds.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400 border rounded-lg bg-gray-50/50">
          <Folder className="h-10 w-10 mb-3 stroke-1" />
          <p className="text-sm font-medium mb-1">No volumes, sections or documents</p>
        </div>
      ) : (
        <div className="space-y-1">
          {rootChildIds.map((id) => (
            <ViewBundleRow key={id} groupId={id} />
          ))}
        </div>
      )}
    </div>
  );
};

const ViewBundleRow = ({ groupId: childId }: { groupId: string }) => {
  const file = useBundleTree((s) => s.config.files[childId]);
  const groupType = useBundleTree((s) => s.config.groups[childId]?.groupType);
  if (file) return <ViewFile file={file} />;
  if (groupType === GroupType.VOLUME) return <ViewVolume groupId={childId} />;
  if (groupType === GroupType.EMAIL) return <ViewEmailGroup groupId={childId} />;
  return <ViewSection groupId={childId} />;
};
